import React, { FC, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDropzone from 'react-dropzone';
import { Button } from '../Button';
import { CameraTimer } from '../CameraTimer';
import { Timer } from '../Timer';
import { IMediaZoomSettings, IWebcamRef, Webcam } from '../Webcam';
import { ImageIcon, InfoCircleIcon, ShootIcon } from '../../icons';
import { useWindowSize } from '../../hooks';
import { useSelector } from '../../redux/store';
import { IPhoto } from '../../resources/interfaces';
import { cropImageFromGeometry } from '../../utils/helpers';

export interface IPhotoScannerProps {
  className?: string;
  header?: ReactNode;
  aspectRatio?: number;
  showWireframe?: boolean;
  onTakePhoto?: (photo: IPhoto) => void;
}

export const PhotoScanner: FC<IPhotoScannerProps> = ({
  className,
  header,
  aspectRatio = 3 / 4,
  showWireframe = true,
  onTakePhoto,
}) => {
  const { t } = useTranslation();
  const { profile } = useSelector((root) => root.patient);

  const { windowWidth } = useWindowSize();
  const ref = useRef<HTMLDivElement>();
  const camRef = useRef<IWebcamRef>();
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [error, setError] = useState<string>();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [maskRect, setMaskRect] = useState({ left: 0, top: 0, right: 0, bottom: 0 });

  const [zoomSetting, setZoomSetting] = useState<IMediaZoomSettings>({
    isSupported: false,
    min: 1,
    max: 1,
    step: 1,
    value: 1,
    onChange: () => {},
  });

  const [timer, setTimer] = useState<number>();
  const [timerPlaying, setTimerPlaying] = useState(false);
  const [photo, setPhoto] = useState<IPhoto>();

  const isMiniMode = useMemo(() => size.height < 1000, [size]);

  const horizontalMode = useMemo(
    () => windowWidth < 1024 && size.width >= size.height * aspectRatio + 70,
    [windowWidth, size, aspectRatio],
  );

  const onInitCamera = useCallback((ref) => {
    camRef.current = ref;
    setZoomSetting({
      ...zoomSetting,
      ...ref.zoomSettings,
    });
    setCameraEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResize = React.useCallback(() => {
    const el = ref.current;
    if (el) {
      const width = el.clientWidth;
      const height = el.clientHeight;

      let w = width - 50;
      let h = width / aspectRatio;
      const maxH = horizontalMode ? height - 80 : isMiniMode ? height - 150 : height - 250;
      if (h > maxH) {
        h = maxH;
        w = h * aspectRatio;
      }

      const left = Math.round((width - w) / 2) - (horizontalMode ? 35 : 0);
      const top = Math.round((height - h) / 2) + (horizontalMode ? 25 : -10);

      setSize({ width, height });
      setMaskRect({
        left,
        right: left + w,
        top,
        bottom: top + h,
      });
    }
  }, [aspectRatio, horizontalMode, isMiniMode]);

  React.useEffect(() => {
    onResize();

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  const maskSvg = useMemo(() => {
    if (!size.width || !size.height) {
      return null;
    }

    const l = maskRect.left / size.width;
    const r = maskRect.right / size.width;
    const t = maskRect.top / size.height;
    const b = maskRect.bottom / size.height;
    const rx = 10 / size.width;
    const ry = 10 / size.height;
    return (
      <svg className="absolute">
        <defs>
          <clipPath id="mask-path" clipPathUnits="objectBoundingBox">
            <path
              d={`M0,0 L0,1 L1,1 L1,0 L0,0
              L${l + rx},${t} L${r - rx},${t} C${r - rx},${t} ${r},${t} ${r}${t + ry}
              L${r},${t + ry} L${r},${b - ry} C${r},${b - ry} ${r},${b} ${r - rx}${b}
              L${r - rx},${b} L${l + rx},${b} C${l + rx},${b} ${l},${b} ${l}${b - ry}
              L${l}${b - ry} L${l},${t + ry} C${l},${t + ry} ${l},${t} ${l + rx}${t}
              Z`}
            />
          </clipPath>
        </defs>
      </svg>
    );
  }, [size, maskRect]);

  const onShot = () => {
    if (timer) {
      setTimerPlaying(true);
    } else {
      onTakeCroppedPhoto();
    }
  };

  const onTimeout = () => {
    setTimerPlaying(false);
    onTakeCroppedPhoto();
  };

  const onTakeCroppedPhoto = () => {
    const video = camRef.current.video;
    const cameraWidth = video.clientWidth;
    const cameraHeight = video.clientHeight;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    // Calculate how the video is scaled to fit the element with object-cover
    const videoAspect = videoWidth / videoHeight;
    const displayAspect = cameraWidth / cameraHeight;

    let scale;
    let offsetX = 0;
    let offsetY = 0;

    if (videoAspect > displayAspect) {
      // Video is wider - height matches, width is cropped
      scale = videoHeight / cameraHeight;
      offsetX = (videoWidth - cameraWidth * scale) / 2;
    } else {
      // Video is taller - width matches, height is cropped
      scale = videoWidth / cameraWidth;
      offsetY = (videoHeight - cameraHeight * scale) / 2;
    }

    // Calculate mask position relative to the video element
    const maskLeftInCamera = maskRect.left - (size.width - cameraWidth) / 2;
    const maskTopInCamera = maskRect.top - (size.height - cameraHeight) / 2;
    const maskWidth = maskRect.right - maskRect.left;
    const maskHeight = maskRect.bottom - maskRect.top;

    // Convert to video coordinates
    let rect = {
      x: maskLeftInCamera * scale + offsetX,
      y: maskTopInCamera * scale + offsetY,
      width: maskWidth * scale,
      height: maskHeight * scale,
    };

    // Clamp values to ensure they're within video bounds
    rect.x = Math.max(0, Math.min(rect.x, videoWidth - rect.width));
    rect.y = Math.max(0, Math.min(rect.y, videoHeight - rect.height));
    rect.width = Math.min(rect.width, videoWidth - rect.x);
    rect.height = Math.min(rect.height, videoHeight - rect.y);

    cropImageFromGeometry(video, rect, 1, 1).then((photo) => {
      setPhoto(photo);
    });
  };

  const onUsePhoto = () => {
    if (onTakePhoto) {
      onTakePhoto(photo);
    }
  };

  const onSelectFile = (files: File[]) => {
    if (files.length) {
      setPhoto({
        blob: files[0],
        dataURL: URL.createObjectURL(files[0]),
        fileName: files[0].name || 'photo.jpeg',
      });
    }
  };

  return (
    <div ref={ref} className={classNames('flex-center relative', className)}>
      {!photo && (
        <>
          <Webcam className="h-full w-full" onInit={onInitCamera} onError={setError} />

          {maskSvg}
          <div
            className="absolute left-0 top-0 h-full w-full bg-black/40"
            style={{
              clipPath: 'url(#mask-path)',
              clipRule: 'evenodd',
              fillRule: 'evenodd',
            }}
          />

          {showWireframe && (
            <div
              className="flex-center absolute"
              style={{
                top: maskRect.top + (isMiniMode ? 10 : 20),
                left: maskRect.left + (isMiniMode ? 10 : 20),
                width: `${maskRect.right - maskRect.left - (isMiniMode ? 20 : 40)}px`,
                height: `${maskRect.bottom - maskRect.top - (isMiniMode ? 20 : 40)}px`,
              }}
            >
              <div
                className={classNames(
                  'absolute left-0 top-0 border-l-4 border-t-4 border-primary',
                  isMiniMode ? 'h-6 w-6' : 'h-8 w-8',
                )}
              />
              <div
                className={classNames(
                  'absolute right-0 top-0 border-r-4 border-t-4 border-primary',
                  isMiniMode ? 'h-6 w-6' : 'h-8 w-8',
                )}
              />
              <div
                className={classNames(
                  'absolute bottom-0 left-0 border-b-4 border-l-4 border-primary',
                  isMiniMode ? 'h-6 w-6' : 'h-8 w-8',
                )}
              />
              <div
                className={classNames(
                  'absolute bottom-0 right-0 border-b-4 border-r-4 border-primary',
                  isMiniMode ? 'h-6 w-6' : 'h-8 w-8',
                )}
              />
              <div className={classNames('absolute w-1 bg-primary', isMiniMode ? '-top-2 h-10' : '-top-4 h-16')} />
              <div
                className={classNames('absolute w-1 bg-primary', isMiniMode ? '-bottom-2 h-10' : '-bottom-4 h-16')}
              />
              <div className={classNames('absolute h-1 bg-primary', isMiniMode ? '-left-2 w-10' : '-left-4 w-16')} />
              <div className={classNames('absolute h-1 bg-primary', isMiniMode ? '-right-2 w-10' : '-right-4 w-16')} />
              <div
                className={classNames(
                  'absolute rounded-full border-4 border-primary',
                  isMiniMode ? 'h-6 w-6' : 'h-8 w-8',
                )}
              />
            </div>
          )}
        </>
      )}

      {error && <div className="card1 absolute px-5 py-4">{error}</div>}

      {cameraEnabled && (
        <>
          <div
            className={classNames(
              'absolute left-0 flex w-full items-center',
              photo ? 'text-primary' : 'text-white',
              isMiniMode ? 'top-4 px-6' : 'top-8 px-10',
            )}
          >
            {header}
            <CameraTimer
              className={classNames('ml-auto', photo && 'pointer-events-none opacity-0')}
              value={timer}
              options={[5, 10]}
              onChanges={setTimer}
            />
          </div>

          {photo ? (
            <div className="flex-center h-full w-full flex-col px-10 pb-10 pt-28">
              <div className="flex-center my-auto h-[60vh] flex-1">
                <img className="max-h-full max-w-full rounded-lg" src={photo.dataURL} alt="" />
              </div>
              <div className="mt-10 flex items-center gap-6">
                <Button className="!w-43" theme="primary" variant="outline" onClick={() => setPhoto(undefined)}>
                  <i className="fa fa-times mr-2 text-2xl" /> {t('common.retake')}
                </Button>
                <Button className="!w-43" theme="primary" onClick={onUsePhoto}>
                  <i className="fa fa-check mr-2 text-2xl" /> {t('common.usePhoto')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {timer && (
                <div className="absolute">
                  <Timer
                    className="text-64p font-semibold text-white"
                    time={timer}
                    playing={timerPlaying}
                    format="s"
                    onFinished={onTimeout}
                  />
                </div>
              )}

              <div
                className={classNames(
                  'flex-center absolute gap-8 lg:gap-12',
                  horizontalMode ? 'right-4 flex-col md:right-8' : isMiniMode ? 'bottom-4' : 'bottom-10',
                )}
              >
                <ReactDropzone
                  accept={{
                    'image/jpeg': ['.jpeg', '.png'],
                  }}
                  onDrop={onSelectFile}
                >
                  {(state) => (
                    <div {...state.getRootProps()}>
                      <input {...state.getInputProps()} />
                      <div className="flex-center h-12 w-12 cursor-pointer rounded-full bg-overlay1 text-white transition-all hover:scale-110">
                        <ImageIcon size={24} />
                      </div>
                    </div>
                  )}
                </ReactDropzone>
                <ShootIcon
                  className="cursor-pointer text-white transition-all hover:scale-110"
                  size={isMiniMode ? 60 : 70}
                  onClick={onShot}
                />
                <Link
                  className="flex-center pointer-events-none h-12 w-12 cursor-pointer rounded-full bg-overlay1 text-white opacity-0 transition-all hover:scale-110"
                  to={`/patients/${profile.id}/scanner/photo-tips`}
                >
                  <InfoCircleIcon size={24} />
                </Link>
              </div>

              <div
                className={classNames('absolute w-24 sm:w-30', isMiniMode ? 'bottom-4 right-4' : 'bottom-10 right-8')}
              >
                <div className="flex items-center justify-between px-1 text-white">
                  <button
                    className="disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={
                      !zoomSetting.isSupported ||
                      parseFloat(zoomSetting.value.toFixed(1)) <= parseFloat(zoomSetting.min.toFixed(1))
                    }
                    onClick={() => {
                      let newValue = zoomSetting.value - zoomSetting.step;
                      if (newValue <= zoomSetting.min) {
                        newValue = zoomSetting.min;
                      }
                      setZoomSetting((prev) => ({ ...prev, value: newValue }));
                      zoomSetting.onChange(newValue);
                    }}
                  >
                    -
                  </button>
                  <button
                    className="disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={
                      !zoomSetting.isSupported ||
                      parseFloat(zoomSetting.value.toFixed(1)) >= parseFloat(zoomSetting.max.toFixed(1))
                    }
                    onClick={() => {
                      let newValue = zoomSetting.value + zoomSetting.step;
                      if (newValue >= zoomSetting.max) {
                        newValue = zoomSetting.max;
                      }
                      setZoomSetting((prev) => ({ ...prev, value: newValue }));
                      zoomSetting.onChange(newValue);
                    }}
                  >
                    +
                  </button>
                </div>
                <input
                  className="w-full"
                  type="range"
                  disabled={!zoomSetting.isSupported}
                  min={zoomSetting.min}
                  max={zoomSetting.max}
                  step={zoomSetting.step}
                  value={zoomSetting.value}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setZoomSetting((prev) => ({ ...prev, value }));
                    zoomSetting.onChange(value);
                  }}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* {croppingImage && (
        <PhotoCropPopup
          photo={croppingImage}
          aspectRatio={aspectRatio}
          onCrop={setPhoto}
          onClose={() => setCroppingImage(undefined)}
        />
      )} */}
    </div>
  );
};
